/**
 * 高考选科 API
 *
 * 提供高考选科任务的 CRUD 操作接口。
 *
 * @module api/subject-selection
 */

import type {
  SubjectSelectionTask,
  SubjectSelectionTaskForm,
  SubjectSelectionRule,
  SubjectSelectionLegalCombination,
  SubjectSelectionValidationResult,
  SubjectSelectionMode,
  SubjectSelectionMethod,
  SubjectSelectionTaskStatus,
  SubjectSelectionTaskType,
  ManagementTaskStatus,
  SubjectSelectionCreationScope,
  SubjectSelectionMemberCandidate,
  SubjectSelectionTaskMember
} from '@/types'
import { gradeApi } from '@/api/data-management'
import { http, buildApiUrl, buildAuthHeaders } from './request'

async function resolveGradeIdByName(gradeName: string): Promise<number | undefined> {
  if (!gradeName?.trim()) return undefined
  try {
    const grades = await gradeApi.list()
    const keyword = gradeName.trim()
    const exact = grades.find((g) =>
      [g.name, g.displayName, g.cohortName]
        .filter((value): value is string => Boolean(value))
        .some((value) => value === keyword)
    )
    if (exact) return exact.id
    const partial = grades.find(
      (g) =>
        [g.name, g.displayName, g.cohortName]
          .filter((value): value is string => Boolean(value))
          .some((value) => value === keyword || keyword.startsWith(value) || value.startsWith(keyword))
    )
    return partial?.id
  } catch {
    return undefined
  }
}

async function readApiErrorMessage(resp: Response, fallback: string): Promise<string> {
  try {
    const body = (await resp.json()) as { message?: string; data?: unknown }
    if (typeof body.message === 'string' && body.message.length > 0) {
      return body.message
    }
  } catch {
    /* ignore */
  }
  return `${fallback}（HTTP ${resp.status}）`
}

const subjectSelectionApi = {
  async listTasks(): Promise<SubjectSelectionTask[]> {
    const response = await http.get<BackendTaskDto[]>('/subject-selection/tasks')
    return response.data.map(mapTask)
  },
  async getTask(id: string): Promise<SubjectSelectionTask> {
    const response = await http.get<BackendTaskDto>(`/subject-selection/tasks/${id}`)
    return mapTask(response.data)
  },
  async createTask(payload: SubjectSelectionTaskForm): Promise<SubjectSelectionTask> {
    const gradeId = await resolveGradeIdByName(payload.grade)
    const response = await http.post<BackendTaskDto>('/subject-selection/tasks', {
      name: payload.name,
      taskLabel: payload.taskLabel || null,
      mode: payload.mode,
      requiredSubjectCount: payload.requiredSubjectCount,
      gradeId: gradeId ?? null,
      gradeName: payload.grade,
      selectionMode: payload.selectionMode,
      selectionStartAt: payload.startTime || null,
      selectionEndAt: payload.endTime || null,
      participantCourseIds: payload.participantCourseIds,
      participantClassIds: payload.participantClassIds ?? [],
      taskType: 'SYSTEM_COLLECTION' satisfies SubjectSelectionTaskType
    })
    return mapTask(response.data)
  },
  async updateTask(id: string, payload: Partial<SubjectSelectionTaskForm>): Promise<SubjectSelectionTask> {
    const gradeId =
      payload.grade !== undefined && payload.grade !== null && payload.grade !== ''
        ? await resolveGradeIdByName(payload.grade)
        : undefined
    const body: Record<string, unknown> = {}
    if (payload.name !== undefined) body.name = payload.name
    if (payload.taskLabel !== undefined) body.taskLabel = payload.taskLabel
    if (payload.mode !== undefined) body.mode = payload.mode
    if (payload.requiredSubjectCount !== undefined) body.requiredSubjectCount = payload.requiredSubjectCount
    if (gradeId !== undefined) body.gradeId = gradeId
    if (payload.grade !== undefined) body.gradeName = payload.grade
    if (payload.selectionMode !== undefined) body.selectionMode = payload.selectionMode
    if (payload.startTime !== undefined) body.selectionStartAt = payload.startTime
    if (payload.endTime !== undefined) body.selectionEndAt = payload.endTime
    if (payload.participantCourseIds !== undefined) body.participantCourseIds = payload.participantCourseIds
    if (payload.participantClassIds !== undefined) body.participantClassIds = payload.participantClassIds
    const response = await http.put<BackendTaskDto>(`/subject-selection/tasks/${id}`, body)
    return mapTask(response.data)
  },
  async publishTask(id: string): Promise<SubjectSelectionTask> {
    const response = await http.post<BackendTaskDto>(`/subject-selection/tasks/${id}/publish`, {})
    return mapTask(response.data)
  },
  async removeTask(id: string): Promise<void> {
    await http.delete<void>(`/subject-selection/tasks/${id}`)
  },
  async listRules(taskId: string): Promise<SubjectSelectionRule[]> {
    const response = await http.get<BackendRuleDto[]>(`/subject-selection/tasks/${taskId}/rules`)
    return response.data
  },
  async createRule(taskId: string, payload: Omit<SubjectSelectionRule, 'id' | 'systemGenerated'>): Promise<SubjectSelectionRule> {
    const response = await http.post<BackendRuleDto>(`/subject-selection/tasks/${taskId}/rules`, payload)
    return response.data
  },
  async updateRule(taskId: string, ruleId: number, payload: Omit<SubjectSelectionRule, 'id' | 'systemGenerated'>): Promise<SubjectSelectionRule> {
    const response = await http.put<BackendRuleDto>(`/subject-selection/tasks/${taskId}/rules/${ruleId}`, payload)
    return response.data
  },
  async deleteRule(taskId: string, ruleId: number): Promise<void> {
    await http.delete<void>(`/subject-selection/tasks/${taskId}/rules/${ruleId}`)
  },
  async applyTemplate(taskId: string): Promise<{ task: SubjectSelectionTask; rules: SubjectSelectionRule[] }> {
    const response = await http.post<{ task: BackendTaskDto; rules: BackendRuleDto[] }>(
      `/subject-selection/tasks/${taskId}/apply-template`,
      {}
    )
    return {
      task: mapTask(response.data.task),
      rules: response.data.rules
    }
  },
  async generateLegalCombinations(taskId: string): Promise<SubjectSelectionLegalCombination[]> {
    const response = await http.get<SubjectSelectionLegalCombination[]>(`/subject-selection/tasks/${taskId}/legal-combinations`)
    return response.data
  },
  async validateSelection(taskId: string, courseIds: number[]): Promise<SubjectSelectionValidationResult> {
    const response = await http.post<SubjectSelectionValidationResult>(`/subject-selection/tasks/${taskId}/validate`, {
      courseIds
    })
    return response.data
  },
  async submitSelection(
    taskId: string,
    studentId: number,
    courseIds: number[]
  ): Promise<SubjectSelectionValidationResult> {
    const response = await http.post<SubjectSelectionValidationResult>(`/subject-selection/tasks/${taskId}/submit`, {
      studentId,
      courseIds
    })
    return response.data
  },

  async downloadDirectImportDraftTemplate(params: {
    gradeName: string
    participantCourseIds?: number[]
    participantClassIds?: number[]
  }): Promise<Blob> {
    const q = new URLSearchParams()
    q.set('gradeName', params.gradeName.trim())
    if (params.participantCourseIds?.length) {
      for (const id of params.participantCourseIds) {
        q.append('participantCourseIds', String(id))
      }
    }
    if (params.participantClassIds?.length) {
      for (const id of params.participantClassIds) {
        q.append('participantClassIds', String(id))
      }
    }
    const url = buildApiUrl(`/subject-selection/direct-import/template?${q.toString()}`)
    const resp = await fetch(url, { method: 'GET', headers: buildAuthHeaders() })
    if (!resp.ok) {
      throw new Error(await readApiErrorMessage(resp, '下载模板失败'))
    }
    return await resp.blob()
  },

  async createTaskFromDirectImport(payload: {
    file: File
    name: string
    taskLabel?: string
    gradeName: string
    mode?: string
    selectionMode?: string
    participantCourseIds?: number[]
    requiredSubjectCount?: number
    rulesJson?: string
    participantClassIds?: number[]
  }): Promise<SubjectSelectionTask> {
    const form = new FormData()
    form.append('file', payload.file)
    form.append('name', payload.name.trim())
    if (payload.taskLabel?.trim()) {
      form.append('taskLabel', payload.taskLabel.trim())
    }
    form.append('gradeName', payload.gradeName.trim())
    if (payload.mode?.trim()) {
      form.append('mode', payload.mode.trim())
    }
    if (payload.selectionMode?.trim()) {
      form.append('selectionMode', payload.selectionMode.trim())
    }
    if (payload.participantCourseIds?.length) {
      for (const id of payload.participantCourseIds) {
        form.append('participantCourseIds', String(id))
      }
    }
    if (payload.requiredSubjectCount != null && payload.requiredSubjectCount > 0) {
      form.append('requiredSubjectCount', String(payload.requiredSubjectCount))
    }
    if (payload.rulesJson?.trim()) {
      form.append('rulesJson', payload.rulesJson.trim())
    }
    if (payload.participantClassIds?.length) {
      for (const id of payload.participantClassIds) {
        form.append('participantClassIds', String(id))
      }
    }
    const url = buildApiUrl('/subject-selection/direct-import/tasks')
    const resp = await fetch(url, { method: 'POST', headers: buildAuthHeaders(), body: form })
    if (!resp.ok) {
      throw new Error(await readApiErrorMessage(resp, '导入创建任务失败'))
    }
    const body = (await resp.json()) as { data: BackendTaskDto }
    return mapTask(body.data)
  },

  async downloadImportTemplate(taskId: string): Promise<Blob> {
    const url = buildApiUrl(`/subject-selection/tasks/${taskId}/import-template`)
    const resp = await fetch(url, { method: 'GET', headers: buildAuthHeaders() })
    if (!resp.ok) {
      throw new Error(await readApiErrorMessage(resp, '下载模板失败'))
    }
    return await resp.blob()
  },

  async previewImport(
    taskId: string,
    file: File
  ): Promise<SubjectSelectionImportPreview> {
    const form = new FormData()
    form.append('file', file)
    const url = buildApiUrl(`/subject-selection/tasks/${taskId}/import/preview`)
    const resp = await fetch(url, { method: 'POST', headers: buildAuthHeaders(), body: form })
    if (!resp.ok) {
      throw new Error(await readApiErrorMessage(resp, '导入解析失败'))
    }
    const payload = (await resp.json()) as { data: SubjectSelectionImportPreview }
    return payload.data
  },

  async confirmImport(
    taskId: string,
    file: File,
    switchToDirectImport = false
  ): Promise<SubjectSelectionImportConfirmResult> {
    const form = new FormData()
    form.append('file', file)
    const q = switchToDirectImport ? '?switchToDirectImport=true' : ''
    const url = buildApiUrl(`/subject-selection/tasks/${taskId}/import/confirm${q}`)
    const resp = await fetch(url, { method: 'POST', headers: buildAuthHeaders(), body: form })
    if (!resp.ok) {
      throw new Error(await readApiErrorMessage(resp, '确认导入失败'))
    }
    const payload = (await resp.json()) as { data: SubjectSelectionImportConfirmResult }
    return payload.data
  },

  async getSubmissionStats(taskId: string): Promise<SubjectSelectionSubmissionStats> {
    const response = await http.get<SubjectSelectionSubmissionStats>(`/subject-selection/tasks/${taskId}/submission-stats`)
    return response.data
  },

  async listSubmissions(taskId: string): Promise<SubjectSelectionSubmission[]> {
    const response = await http.get<SubjectSelectionSubmission[]>(`/subject-selection/tasks/${taskId}/submissions`)
    return response.data
  },

  async listPendingStudents(taskId: string): Promise<SubjectSelectionPendingStudent[]> {
    const response = await http.get<SubjectSelectionPendingStudent[]>(
      `/subject-selection/tasks/${taskId}/pending-students`
    )
    return response.data
  },

  /** 创建任务圈人范围：届别下未归档班级（含在读人数）+ 未分班学生数 */
  async getCreationScope(gradeId: number): Promise<SubjectSelectionCreationScope> {
    const response = await http.get<SubjectSelectionCreationScope>(
      `/subject-selection/scope/classes?gradeId=${gradeId}`
    )
    return response.data
  },

  /** 圈人候选学生（classIds 为空时取该届别全部未归档班级学生 + 未分班学生） */
  async listScopeStudents(gradeId: number, classIds?: number[]): Promise<SubjectSelectionMemberCandidate[]> {
    const q = new URLSearchParams()
    q.set('gradeId', String(gradeId))
    if (classIds?.length) {
      for (const id of classIds) {
        q.append('classIds', String(id))
      }
    }
    const response = await http.get<SubjectSelectionMemberCandidate[]>(
      `/subject-selection/scope/students?${q.toString()}`
    )
    return response.data
  },

  async listMembers(taskId: string): Promise<SubjectSelectionTaskMember[]> {
    const response = await http.get<SubjectSelectionTaskMember[]>(
      `/subject-selection/tasks/${taskId}/members`
    )
    return response.data
  },

  async addMembers(taskId: string, studentIds: number[]): Promise<number> {
    const response = await http.post<number>(`/subject-selection/tasks/${taskId}/members`, { studentIds })
    return response.data
  },

  async removeMember(taskId: string, studentId: number): Promise<void> {
    await http.delete<void>(`/subject-selection/tasks/${taskId}/members/${studentId}`)
  }
} as const

export interface SubjectSelectionImportPreviewRow {
  studentId: number
  studentNo: string
  studentName: string
  selectedCourseIds: number[]
}

export interface SubjectSelectionImportPreview {
  total: number
  success: number
  failed: number
  rows: SubjectSelectionImportPreviewRow[]
  errors: Array<{ row: number; message: string }>
}

interface SubjectSelectionImportConfirmResult {
  total: number
  created: number
  updated: number
  errors: Array<{ row: number; message: string }>
}

export interface SubjectSelectionSubmissionStats {
  totalSubmissions: number
  targetStudentCount: number
  completedInGradeCount: number
  pendingCount: number
  comboStats: Array<{
    combination: string
    count: number
    courseIds: number[]
    courseNames: string[]
  }>
}

export interface SubjectSelectionPendingStudent {
  studentId: number
  studentNo: string | null
  studentName: string | null
  classId: number | null
  className: string | null
}

export interface SubjectSelectionSubmission {
  id: number
  studentId: number
  studentNo: string | null
  studentName: string | null
  classId: number | null
  className: string | null
  selectedCourseIds: number[]
  selectedCourses: string[]
  submittedAt: string
}

type BackendTaskDto = {
  id: number
  name: string
  taskLabel: string | null
  mode: SubjectSelectionMode
  requiredSubjectCount: number
  gradeId: number | null
  gradeName: string | null
  selectionMode: SubjectSelectionMethod
  taskType?: SubjectSelectionTaskType
  status: SubjectSelectionTaskStatus
  selectionStartAt: string | null
  selectionEndAt: string | null
  participantCourseIds: number[]
  participantCourses: string[]
  createdAt: string
  scope?: Record<string, unknown> | null
}

type BackendRuleDto = SubjectSelectionRule

function mapTask(dto: BackendTaskDto): SubjectSelectionTask {
  const statusMap: Record<SubjectSelectionTaskStatus, ManagementTaskStatus> = {
    DRAFT: 'draft',
    PUBLISHED: 'published',
    IN_PROGRESS: 'processing',
    CLOSED: 'closed',
    FINISHED: 'completed'
  }
  return {
    id: String(dto.id),
    name: dto.name,
    taskType: dto.taskType ?? 'SYSTEM_COLLECTION',
    status: statusMap[dto.status] ?? 'draft',
    statusText: dto.status,
    gradeId: dto.gradeId ?? undefined,
    grade: dto.gradeName ?? '',
    taskLabel: dto.taskLabel ?? '',
    createdAt: dto.createdAt,
    dateRange: `${dto.selectionStartAt ?? '-'} ~ ${dto.selectionEndAt ?? '-'}`,
    mode: dto.mode,
    selectionMode: dto.selectionMode,
    requiredSubjectCount: dto.requiredSubjectCount,
    participantCourseIds: dto.participantCourseIds ?? [],
    participantCourses: dto.participantCourses ?? [],
    startTime: dto.selectionStartAt ?? '',
    endTime: dto.selectionEndAt ?? '',
    scope: dto.scope ?? undefined
  }
}

// ============================================================================
export { subjectSelectionApi }
